import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ClientError } from '~/errors';
import { useHttpClient } from '~/hooks/useHttpClient';
import { QUERY_KEY as USER_PROFILE_QUERY_KEY } from '~/services/useUserProfileQuery';
import { sanitizeTagValue } from '~/utils/instrumentation';
import { createLogger } from '~/utils/logging';

const log = createLogger('useAdministratorSetupMutation');

export const MUTATION_KEY = 'administratorSetupMutation';

export type AdministratorSetupRequest = {
  name: string;
  referredToName: string;
  tosAgreedAt: string;
  insightsEmailOptIn: boolean;
};

type AdministratorSetupResponse = {
  schoolAdmin: {
    id: string;
  };
};

export function useAdministratorSetupMutation() {
  const queryClient = useQueryClient();
  const httpClient = useHttpClient();

  const { data, error, mutate, reset, status } = useMutation({
    mutationKey: [MUTATION_KEY],
    mutationFn: async (request: AdministratorSetupRequest) => {
      log.debug('Setting up administrator');
      const { schoolAdmin } = await httpClient.post<AdministratorSetupResponse>('/admin/setup', request);
      log.info('Set up administrator successfully', {
        userId: schoolAdmin.id,
      });
      return { schoolAdmin };
    },
    onSuccess: async () => {
      // Invalidate the user profile query so that user.isSetupComplete is updated.
      await queryClient.invalidateQueries({ queryKey: [USER_PROFILE_QUERY_KEY] });
    },
    onError: (error: Error) => {
      if (error instanceof ClientError) {
        // Ignore client errors.
      } else {
        log.error('Failed to set up administrator', {
          errorMessage: sanitizeTagValue(error.message),
        });
      }
    },
  });

  return {
    error,
    mutate,
    reset,
    status,
    schoolAdmin: data?.schoolAdmin,
  };
}
